import { Text, View } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";
import { DRUCKPFAHL } from "../../../constants";
import { BemessungssituationState } from "../../../store/slices/innereSlice";
import KiloNewton from "../../Math/Units/Metric/KiloNewton";
import Millimetre from "../../Math/Units/Metric/Millimetre";
import { DataRow, DataRowTextValue } from "../DataRow";
import { GermanyInnereDruckKnickenDinEN1993 } from "./GermanyInnereDruckKnickenDin";
import { GermanyInnereDruckKnickenOfner } from "./GermanyInnereDruckKnickenOfner";
import InvoiceTableBlankSpace from "../InvoiceTableBlankSpace";
import { GermanyInnereErmuedung } from "./GermanyInnereErmuedung";
import { PageWrapper, styles, tableStyles } from "../SmartTitanProjektPDF";

export function GermanyInnere({ data }) {
  const { t } = useTranslation("translation");
  let itemIndex = 1;

  const showKnicken = data?.FrontendZustand?.knickenCheckbox === true ?? false;
  const isOfner = data?.FrontendZustand?.knickenTypeRadio == 1 ?? false;
  const isCompression =
    data.Germany.Innere.BeanspruchungausStatik.belastungsartUndRichtung === DRUCKPFAHL;
  const isGermany = data.Deckblatt.BemessungenMikropfaehle === 1;
  const showErmuedung = data?.FrontendZustand?.ermuedungCheckbox === true ?? false;
  return (
    <>
      <PageWrapper Deckblatt={data.Deckblatt}>
        <View>
          {/* Innere Tragfähigkeit */}
          <Text style={styles.section}>{t("inner.headline")}</Text>

          {/* Beanspruchung aus Statik */}
          <Text style={styles.sectionSubText}>{t("inner.designLoad.headline_pdf")}</Text>

          <View style={tableStyles.tableContainer}>
            {/* Belastungsart/-Richtung */}
            <DataRowTextValue
              item={{
                title: t("inner.loadType"),
                value: t(
                  data.Germany.Innere.BeanspruchungausStatik.belastungsartUndRichtung === DRUCKPFAHL
                    ? "inner.compressionPile"
                    : "inner.tensionPile"
                ),
                itemIndex: itemIndex++,
              }}
            />

            {/* Bemessungssituation */}
            {isGermany && (
              <DataRowTextValue
                item={{
                  title: t("inner.dimensioningSituation"),
                  value: t(
                    data.Germany.Innere.BeanspruchungausStatik.bemessungssituation ===
                      BemessungssituationState.DAUERHAFT
                      ? "inner.permanent"
                      : "inner.temporary"
                  ),
                  itemIndex: itemIndex++,
                }}
              />
            )}

            {/* Bemessungslast */}
            <DataRow
              item={{
                title: t("inner.fdCalculation.designLoad"),
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\inline&space;\\small&space;\\dpi{110}\\bg{white}F_{d}",
                  height: 7,
                  width: 9,
                },
                value: data.Germany.Innere.BeanspruchungausStatik.bemessungslast,
                // formulaImage: {
                //   uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;F_d=\\gamma_G\\cdot&space;E_{k,G}&plus;\\gamma_Q\\cdot&space;E_{k,Q}&plus;\\gamma_{G,E_0}-\\gamma_{G,inf}\\cdot&space;E_{k,G,inf}",
                //   height: 9,
                //   width: 192,
                // },
                valueScale: 2,
                itemIndex: itemIndex++,
                valueUnit: <KiloNewton />,
              }}
            />
          </View>

          {/* Nachweis Stahltragglied */}
          <Text style={styles.sectionSubText}>{t("inner.verificationCapacity.headline")}</Text>

          <View style={tableStyles.tableContainer}>
            {/* Wahl des Stahltraggliedes */}
            <DataRowTextValue
              item={{
                title: t("inner.verificationCapacity.selectedLoadCarryingElement"),
                // bezImage: {
                //   uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}c_{req}",
                //   height: 9,
                //   width: 15,
                // },
                value: data.Germany.Innere.NachweisStahltragglied.wahlDesStahltraggliedes,

                itemIndex: itemIndex++,
              }}
            />

            {/* Erforderliche Zementsteinüberdeckung */}
            <DataRow
              item={{
                title: t("inner.verificationCapacity.requiredGroutCover"),
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}c_{req}",
                  height: 7,
                  width: 12,
                },
                value:
                  data.Germany.Innere.NachweisStahltragglied.erforderlicheZementsteinueberdeckung,
                valueScale: 0,
                itemIndex: itemIndex++,
                valueUnit: <Millimetre />,
              }}
            />

            {/* Bohrkronendurchmesser */}
            <DataRow
              item={{
                title: t("inner.verificationCapacity.drillBitDiameter"),
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}D",
                  height: 6,
                  width: 7,
                },
                value: data.Germany.Innere.NachweisStahltragglied.bohrkronendurchmesser,
                valueScale: 0,
                itemIndex: itemIndex++,
                valueUnit: <Millimetre />,
              }}
            />

            {/* Durchmesser Stahltragglied */}
            <DataRow
              item={{
                title: t("inner.verificationCapacity.hollowBarDiameter"),
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}D_{a}",
                  height: 7,
                  width: 10,
                },
                value: data.Germany.Innere.NachweisStahltragglied.durchmesserStahltragglied,
                valueScale: 2,
                itemIndex: itemIndex++,
                valueUnit: <Millimetre />,
              }}
            />

            {/* Aufweitung nach DIN SPEC 18539 */}
            <DataRow
              item={{
                title: t("inner.verificationCapacity.widening_ec7"),
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}a_{min}",
                  height: 5,
                  width: 16,
                },
                value: data.Germany.Innere.NachweisStahltragglied.aufweitungNnachDIN_SPEC_18539,
                valueScale: 0,
                itemIndex: itemIndex++,
                valueUnit: <Millimetre />,
              }}
            />

            {/* Vorhandene Zementsteinüberdeckung */}
            <DataRow
              item={{
                title: t("inner.verificationCapacity.existingGroutCover"),
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}c_{nom}",
                  height: 5,
                  width: 16,
                },
                value: data.Germany.Innere.NachweisStahltragglied.vorhandeneZementsteinueberdeckung,
                valueScale: 0,
                itemIndex: itemIndex++,
                formulaImage: {
                  uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}c_{nom}&space;=&space;\\frac{D&plus;a_{min}-D_a}{2}",
                  height: 11,
                  width: 70,
                },
                valueUnit: <Millimetre />,
              }}
            />

            {/* Charakteristische Tragfähigkeit */}
            <DataRow
              item={{
                title: t("inner.verificationCapacity.characteristicLoadCarryingCapacity"),
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}R_{k}",
                  height: 7,
                  width: 10,
                },
                value: data.Germany.Innere.NachweisStahltragglied.charakteristischeTragfaehigkeit,
                valueScale: 0,
                itemIndex: itemIndex++,
                valueUnit: <KiloNewton />,
              }}
            />

            {/* Teilsicherheitsbeiwert */}
            <DataRow
              item={{
                title: t("inner.verificationCapacity.partialSafetyFactor"),
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\gamma_{&space;_{M}}",
                  height: 6,
                  width: 10,
                },
                value: data.Germany.Innere.NachweisStahltragglied.teilsicherheitsbeiwert,
                valueScale: 2,
                itemIndex: itemIndex++,
              }}
            />

            {/* Bemessungswert der Tragfähigkeit */}
            <DataRow
              item={{
                title: t("inner.verificationCapacity.designLoadCarryingCapacity"),
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}R_d",
                  height: 7,
                  width: 10,
                },
                value: data.Germany.Innere.NachweisStahltragglied.bemessungswertDerTragfaehigkeit,
                valueScale: 0,
                itemIndex: itemIndex++,
                formulaImage: {
                  uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}R_d&space;=&space;\\frac{R_k}{\\gamma_{&space;_{M}}",
                  height: 13,
                  width: 32,
                },
                valueUnit: <KiloNewton />,
              }}
            />

            {/* Ausnutzung */}
            <DataRow
              item={{
                title: t("inner.verificationCapacity.utilizationFactor.headline"),
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\eta",
                  height: 6,
                  width: 4,
                },
                value: data.Germany.Innere.NachweisStahltragglied.ausnutzung,
                valueScale: 2,
                itemIndex: itemIndex++,
                formulaImage: {
                  uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\eta&space;=&space;\\frac{F_d}{R_d}",
                  height: 12,
                  width: 25,
                },
                // valueUnit: <KiloNewton />,
              }}
            />
            <InvoiceTableBlankSpace rowsCount={25 - itemIndex} />
          </View>
        </View>
      </PageWrapper>

      {showKnicken && isCompression ? (
        <>
          {isOfner ? (
            <GermanyInnereDruckKnickenOfner data={data} />
          ) : (
            <GermanyInnereDruckKnickenDinEN1993 data={data} />
          )}
        </>
      ) : null}

      {showErmuedung && <GermanyInnereErmuedung data={data} />}
    </>
  );
}
